/**
 * Handles redirects for unauthenticated users
 * Blocks Router when auth is unresolved or redirecting
 * Single responsibility: prevent Router from rendering until ready
 */
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect, useMemo } from 'react'

import { useLoadingStateContext } from '@/providers/loading/useLoadingStateContext'
import { getPathInfo } from '@/utils/routeUtils'

import Router from './Router'

/**
 * Wrapper component that defers Router rendering until the global loader completes.
 * This prevents pages from mounting and making HTTP requests during the logo animation,
 * reducing CPU/GPU competition and improving animation frame rates and page painting.
 */
export function DeferredRouter(): ReactNode {
  const { isAuthenticated } = useKindeAuth()
  const { isGlobalLoaderActive } = useLoadingStateContext()

  // Compute path info once
  const pathInfo = useMemo(() => getPathInfo(), [])

  // Redirect immediately when auth is resolved as false on protected routes
  // This prevents Router from ever rendering, eliminating dashboard flash
  useEffect(() => {
    if (!pathInfo.isProtectedRoute) {
      return
    }

    if (isAuthenticated === false) {
      window.location.replace('/sign-in')
    }
  }, [isAuthenticated, pathInfo.isProtectedRoute])

  // Block Router on /auth/callback, when loader is active, or when auth is unresolved on protected routes
  // Blocking when isAuthenticated === false prevents Router from rendering before redirect completes
  const isAuthUnresolved = pathInfo.isProtectedRoute && (isAuthenticated === undefined || isAuthenticated === false)
  const shouldBlockRouter = pathInfo.isAuthCallback || isGlobalLoaderActive || isAuthUnresolved

  // Don't render Router until global loader completes
  if (shouldBlockRouter) {
    return null
  }

  return <Router />
}
